// Super-admin → Company → Storage Configuration (S58).
//
// Lets a super-admin configure per-company object storage on behalf of a
// tenant. Talks to /api/v1/super/companies/:enc_id/storage.
// Credentials are write-only — the API never returns key values.

import { useEffect, useState, type FormEvent } from 'react';
import { ArrowLeft, Plug, Save, Trash2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Input } from '../../../components/form/Input';
import { Select } from '../../../components/form/Select';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { ConfirmModal } from '../../../components/ui/ConfirmModal/ConfirmModal';
import { useCompanyDetail } from '../hooks/useSuper';
import {
  useDeleteSuperCompanyStorage,
  useSuperCompanyStorageConfig,
  useTestSuperCompanyStorage,
  useUpdateSuperCompanyStorage,
} from './useSuperCompanyStorage';
import type {
  StorageConfigInput,
  StorageType,
  TenantStorageConfigPublic,
} from '../../../types/api';
import styles from './SuperCompanyStoragePage.module.css';

interface FormState {
  storage_type: StorageType;
  endpoint: string;
  bucket: string;
  access_key: string;
  secret_key: string;
  use_ssl: boolean;
  region: string;
  public_base_url: string;
}

const EMPTY_FORM: FormState = {
  storage_type: 'platform',
  endpoint: '',
  bucket: '',
  access_key: '',
  secret_key: '',
  use_ssl: true,
  region: 'us-east-1',
  public_base_url: '',
};

const STORAGE_TYPE_OPTIONS = [
  { value: 'platform', label: 'Platform Storage (Shared)' },
  { value: 'minio', label: 'MinIO / S3-compatible' },
  { value: 'local', label: 'Local Disk' },
] as const;

function formFromConfig(config: TenantStorageConfigPublic | null): FormState {
  if (!config) return EMPTY_FORM;
  return {
    storage_type: config.storage_type,
    endpoint: config.endpoint ?? '',
    bucket: config.bucket ?? '',
    access_key: '',
    secret_key: '',
    use_ssl: config.use_ssl,
    region: config.region ?? 'us-east-1',
    public_base_url: config.public_base_url ?? '',
  };
}

function endpointDomain(endpoint: string | null): string {
  if (!endpoint) return 'custom';
  try {
    return new URL(endpoint).host;
  } catch {
    return endpoint;
  }
}

function buildInput(form: FormState): StorageConfigInput {
  if (form.storage_type === 'platform') {
    return { storage_type: 'platform' };
  }
  const body: StorageConfigInput = {
    storage_type: form.storage_type,
    use_ssl: form.use_ssl,
    region: form.region,
  };
  if (form.endpoint) body.endpoint = form.endpoint;
  if (form.bucket) body.bucket = form.bucket;
  if (form.public_base_url) body.public_base_url = form.public_base_url;
  if (form.access_key) body.access_key = form.access_key;
  if (form.secret_key) body.secret_key = form.secret_key;
  return body;
}

export function SuperCompanyStoragePage(): JSX.Element {
  const { enc_id: encId = '' } = useParams<{ enc_id: string }>();
  const navigate = useNavigate();

  const detail = useCompanyDetail(encId);
  const query = useSuperCompanyStorageConfig(encId);
  const update = useUpdateSuperCompanyStorage(encId);
  const test = useTestSuperCompanyStorage(encId);
  const revert = useDeleteSuperCompanyStorage(encId);

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [testResult, setTestResult] = useState<{ ok: boolean; text: string } | null>(null);
  const [confirmRevertOpen, setConfirmRevertOpen] = useState(false);

  useEffect(() => {
    if (query.data === undefined) return;
    setForm(formFromConfig(query.data ?? null));
  }, [query.data]);

  if (query.isLoading) {
    return (
      <div className={styles.page}>
        <PageHeader title="Storage Configuration" />
        <Card>
          <div className={styles.empty}>
            <Spinner />
          </div>
        </Card>
      </div>
    );
  }

  const companyName = detail.data?.name;
  const config = query.data ?? null;
  const isPlatform = config === null || config.storage_type === 'platform';
  const isCustom = !isPlatform;
  const showBackendFields = form.storage_type !== 'platform';

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    setTestResult(null);
  }

  async function handleTest() {
    setTestResult(null);
    const result = await test.mutateAsync(buildInput(form), {
      onError: () => {
        setTestResult({ ok: false, text: 'Could not run the connection test. Try again.' });
      },
    });
    if (result.ok) {
      setTestResult({ ok: true, text: 'Connection OK' });
    } else {
      setTestResult({ ok: false, text: result.error || 'Connection failed.' });
    }
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    update.mutate(buildInput(form), {
      onSuccess: () => {
        setTestResult(null);
        void query.refetch();
      },
    });
  }

  function handleRevertConfirmed() {
    return revert.mutateAsync(undefined, {
      onSuccess: () => {
        setConfirmRevertOpen(false);
        setTestResult(null);
        void query.refetch();
      },
    });
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Storage Configuration"
        subtitle={companyName ?? encId}
        actions={
          <Button
            variant="ghost"
            icon={ArrowLeft}
            onClick={() => navigate(`/super/companies/${encId}`)}
          >
            Back
          </Button>
        }
      />

      <Card title="Current Storage">
        <div className={styles.statusRow}>
          {isPlatform ? (
            <>
              <span className={`${styles.statusBadge} ${styles.statusPlatform}`}>
                Platform
              </span>
              <span>Platform Storage (Shared)</span>
            </>
          ) : (
            <>
              <span className={`${styles.statusBadge} ${styles.statusCustom}`}>
                Custom
              </span>
              <span>Custom Storage ({endpointDomain(config!.endpoint)})</span>
            </>
          )}
        </div>

        {isCustom && config ? (
          <dl className={styles.summary}>
            <div className={styles.summaryRow}>
              <dt>Type</dt>
              <dd>{config.storage_type}</dd>
            </div>
            <div className={styles.summaryRow}>
              <dt>Endpoint</dt>
              <dd>{config.endpoint ?? '—'}</dd>
            </div>
            <div className={styles.summaryRow}>
              <dt>Bucket</dt>
              <dd>{config.bucket ?? '—'}</dd>
            </div>
            <div className={styles.summaryRow}>
              <dt>Region</dt>
              <dd>{config.region || '—'}</dd>
            </div>
            <div className={styles.summaryRow}>
              <dt>SSL</dt>
              <dd>{config.use_ssl ? 'Enabled' : 'Disabled'}</dd>
            </div>
            <div className={styles.summaryRow}>
              <dt>Public Base URL</dt>
              <dd>{config.public_base_url ?? '—'}</dd>
            </div>
            <div className={styles.summaryRow}>
              <dt>Credentials</dt>
              <dd>{config.has_credentials ? 'Stored' : 'Not stored'}</dd>
            </div>
          </dl>
        ) : null}
      </Card>

      <Card title="Update Configuration">
        <form onSubmit={handleSubmit} className={styles.form}>
          <Select
            label="Storage Type"
            options={[...STORAGE_TYPE_OPTIONS]}
            value={form.storage_type}
            onChange={(e) => setField('storage_type', e.target.value as StorageType)}
            helper="Platform storage uses the shared bucket. MinIO / local point at the company's own backend."
          />

          {showBackendFields ? (
            <>
              <h3 className={styles.subhead}>Connection</h3>
              <div className={styles.row}>
                <Input
                  label="Endpoint"
                  value={form.endpoint}
                  onChange={(e) => setField('endpoint', e.target.value)}
                  placeholder="https://s3.example.com"
                  helper="Full URL of the storage endpoint."
                />
                <Input
                  label="Bucket"
                  value={form.bucket}
                  onChange={(e) => setField('bucket', e.target.value)}
                />
              </div>
              <div className={styles.row}>
                <Input
                  label="Region"
                  value={form.region}
                  onChange={(e) => setField('region', e.target.value)}
                  helper="e.g. us-east-1"
                />
                <Input
                  label="Public Base URL"
                  value={form.public_base_url}
                  onChange={(e) => setField('public_base_url', e.target.value)}
                  placeholder="https://cdn.example.com"
                  helper="Optional. Full URL used to build public links to stored files."
                />
              </div>

              <h3 className={styles.subhead}>Credentials</h3>
              <div className={styles.row}>
                <Input
                  label="Access Key"
                  variant="password"
                  autoComplete="off"
                  value={form.access_key}
                  onChange={(e) => setField('access_key', e.target.value)}
                  placeholder="Enter new key to update — leave blank to keep existing"
                />
                <Input
                  label="Secret Key"
                  variant="password"
                  autoComplete="off"
                  value={form.secret_key}
                  onChange={(e) => setField('secret_key', e.target.value)}
                  placeholder="Enter new key to update — leave blank to keep existing"
                />
              </div>

              <label className={styles.toggleRow}>
                <input
                  type="checkbox"
                  checked={form.use_ssl}
                  onChange={(e) => setField('use_ssl', e.target.checked)}
                />
                <span>Use SSL (HTTPS) for storage connections</span>
              </label>
            </>
          ) : (
            <p className={styles.muted}>
              Files will be stored in the shared platform bucket. Switch to MinIO
              or Local Disk to use the company's own storage backend.
            </p>
          )}

          {testResult ? (
            <p
              className={`${styles.testResult} ${
                testResult.ok ? styles.testOk : styles.testError
              }`}
              role={testResult.ok ? 'status' : 'alert'}
            >
              {testResult.ok ? '✓ ' : '✗ '}
              {testResult.text}
            </p>
          ) : null}

          <div className={styles.footer}>
            {isCustom ? (
              <Button
                type="button"
                variant="danger"
                icon={Trash2}
                className={styles.footerLead}
                onClick={() => setConfirmRevertOpen(true)}
              >
                Revert to Platform Storage
              </Button>
            ) : null}
            <Button
              type="button"
              variant="secondary"
              icon={Plug}
              loading={test.isPending}
              onClick={() => void handleTest()}
            >
              Test Connection
            </Button>
            <Button
              type="submit"
              variant="primary"
              icon={Save}
              loading={update.isPending}
            >
              Save Configuration
            </Button>
          </div>
        </form>
      </Card>

      <ConfirmModal
        open={confirmRevertOpen}
        title="Revert to Platform Storage"
        message="This will revert all file storage for this company to the shared platform bucket. Continue?"
        confirmLabel="Revert"
        variant="danger"
        loading={revert.isPending}
        onConfirm={handleRevertConfirmed}
        onCancel={() => setConfirmRevertOpen(false)}
      />
    </div>
  );
}
